// Reusable contact picker modal. Lists the user's synced contacts with a
// search box and a "Create new contact" button at the bottom.
//
// Props:
//   userId               — current user's id (used to load their contacts)
//   existingCustomerIds  — contact IDs already attached (filtered out of list)
//   onClose              — called when the user dismisses the modal
//   onPick(contactId)    — called when the user clicks a contact row
//   onCreateNew()        — called when the user clicks the create CTA
//
import { useEffect, useMemo, useState } from "react";
import { Search, X, UserPlus } from "lucide-react";
import { getAllContactsFromFirebase } from "../../utils/firebasefunctions";

export default function AddClientPicker({
  userId,
  existingCustomerIds = [],
  onClose,
  onPick,
  onCreateNew,
}) {
  const [allContacts, setAllContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await getAllContactsFromFirebase(userId);
        if (!cancelled) setAllContacts(list || []);
      } catch (err) {
        console.error("Failed to load contacts:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  const filtered = useMemo(() => {
    const existing = new Set(existingCustomerIds.map(String));
    const q = query.trim().toLowerCase();
    const normalizedQ = q.replace(/\D/g, "");

    return allContacts
      .filter((c) => !existing.has(String(c.id)))
      .filter((c) => {
        if (!q) return true;
        const name = (c.name || "").toLowerCase();
        const email = (c.email || "").toLowerCase();
        const phone = (c.phone || "").replace(/\D/g, "");
        if (normalizedQ && phone.includes(normalizedQ)) return true;
        return name.includes(q) || email.includes(q);
      })
      .sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        }),
      )
      .slice(0, 50);
  }, [allContacts, query, existingCustomerIds]);

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-50"
        onClick={onClose}
      />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden pointer-events-auto flex flex-col max-h-[80vh]">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
            <div className="text-base font-semibold text-slate-900">
              Add Client
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-slate-700 p-1"
            >
              <X size={18} />
            </button>
          </div>

          <div className="px-5 py-3 border-b border-slate-100">
            <div className="relative">
              <Search
                size={13}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
              />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by name, email, or phone…"
                autoFocus
                className="w-full bg-slate-50 border border-slate-200 focus:bg-white focus:border-slate-400 rounded-md pl-8 pr-3 py-2 text-sm outline-none transition"
              />
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="px-5 py-8 text-center text-sm text-slate-500">
                Loading contacts…
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-5 py-8 text-center text-sm text-slate-500">
                {query
                  ? `No contacts match "${query}"`
                  : "No available contacts. Create a new one below."}
              </div>
            ) : (
              <ul>
                {filtered.map((c) => (
                  <li
                    key={c.id}
                    onClick={() => onPick(c.id)}
                    className="px-5 py-2.5 cursor-pointer hover:bg-slate-50 border-b border-slate-100 last:border-b-0"
                  >
                    <div className="text-sm font-medium text-slate-900">
                      {c.name || "(no name)"}
                    </div>
                    <div className="text-[11.5px] text-slate-500 flex gap-3 mt-0.5">
                      {c.email && <span>{c.email}</span>}
                      {c.phone && <span>{c.phone}</span>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="border-t border-slate-200 px-5 py-3 bg-slate-50">
            <button
              type="button"
              onClick={onCreateNew}
              className="w-full inline-flex items-center justify-center gap-2 bg-[#1E293B] hover:opacity-90 text-white rounded-md px-3 py-2 text-sm font-semibold transition"
            >
              <UserPlus size={14} /> Create new contact
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
